<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="ca-breadcrumbs-container">
        <Breadcrumbs />
      </div>

      <!-- Hero Guide Card -->
      <v-card class="ca-hero-card mb-6" elevation="4">
        <div class="ca-hero-header">
          <div class="ca-hero-header-content">
            <div class="d-flex align-center">
              <v-avatar size="52" class="mr-4 ca-hero-logo">
                <v-icon size="30" color="white">mdi-shield-check</v-icon>
              </v-avatar>
              <div>
                <h1 class="ca-hero-title">Compliance Assessment</h1>
                <p class="ca-hero-subtitle">
                  Evaluate &amp; demonstrate compliance with security standards
                </p>
              </div>
            </div>
          </div>
          <v-btn class="ca-new-btn" @click="showNewAssessmentDialog = true">
            <v-icon left size="20">mdi-plus-circle</v-icon>
            {{ $t('conformityAssessment.button') }}
          </v-btn>
        </div>
        <v-card-text class="pt-5 pb-6 px-6">
          <p class="ca-intro-text mb-5">
            Compliance Assessment is the process of evaluating your system or product against
            security standards and regulations such as IEC 62443 or the Cyber Resilience Act (CRA).
            This structured approach helps you demonstrate your compliance through evidence-based
            security assurance.
          </p>
          <v-row>
            <v-col cols="12" md="6">
              <div class="ca-feature-box">
                <div class="ca-feature-header">
                  <div class="ca-feature-icon-box ca-icon-info">
                    <v-icon color="white" size="24">mdi-information-outline</v-icon>
                  </div>
                  <span class="ca-feature-title">What is a Compliance Assessment?</span>
                </div>
                <ul class="ca-feature-list">
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>A structured
                    evaluation of your security controls and practices
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Maps your
                    system to specific requirements from security standards
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Documents
                    evidence that demonstrates your compliance
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Provides
                    measurable results and compliance scores
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Identifies
                    gaps and areas for security improvement
                  </li>
                </ul>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="ca-feature-box">
                <div class="ca-feature-header">
                  <div class="ca-feature-icon-box ca-icon-success">
                    <v-icon color="white" size="24">mdi-checkbox-marked-circle-outline</v-icon>
                  </div>
                  <span class="ca-feature-title">Compliance Assessment Process</span>
                </div>
                <ol class="ca-workflow-list">
                  <li>
                    <span class="ca-step-num">1</span>Create a Subject of Certification (SoC), or
                    select an already defined one
                  </li>
                  <li>
                    <span class="ca-step-num">2</span>Select applicable requirements from the
                    standard
                  </li>
                  <li>
                    <span class="ca-step-num">3</span>Build a Security Assurance Case based on this
                    Subject of Certification
                  </li>
                  <li>
                    <span class="ca-step-num">4</span>Add claims, sub-claims, and evidence to
                    demonstrate compliance
                  </li>
                  <li>
                    <span class="ca-step-num">5</span>Generate reports and visualizations of
                    compliance status
                  </li>
                </ol>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Data Table Card -->
      <v-card class="ca-table-card mb-6" elevation="3">
        <div class="ca-table-header">
          <v-icon color="white" size="24" class="mr-3">mdi-table-large</v-icon>
          <span class="ca-table-header-text">{{ $tc('conformityAssessment.title') }}</span>
          <v-spacer></v-spacer>
          <v-chip dark small class="ca-count-chip">
            <v-icon left x-small>mdi-counter</v-icon>
            {{ assets.length }} Assessments
          </v-chip>
        </div>
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-4" style="gap: 12px;">
            <v-select
              v-model="searchField"
              :items="searchFields"
              item-text="text"
              item-value="value"
              :label="$t('search-field')"
              hide-details
              outlined
              dense
              class="ca-search-field"
              style="max-width: 200px;"
            ></v-select>
            <v-text-field
              v-model="searchQuery"
              append-icon="mdi-magnify"
              :label="$t('search')"
              hide-details
              class="flex-grow-1"
              outlined
              dense
              @keyup.enter="applySearch"
            />
          </div>
          <v-data-table
            :headers="getHeaders"
            :items="assets"
            :search="searchQuery"
            :custom-filter="customFilter"
            class="ca-data-table elevation-0"
            :loading="loading"
            :items-per-page="5"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'mdi-arrow-collapse-left',
              lastIcon: 'mdi-arrow-collapse-right',
              prevIcon: 'mdi-arrow-left',
              nextIcon: 'mdi-arrow-right',
              disableItemsPerPage: false,
              itemsPerPageText: getItemsPerPageText,
              showCurrentPage: true,
            }"
            must-sort
            :sort-desc="true"
          >
            <template v-slot:[`item.score`]="{ item }">
              <v-chip
                :color="item.score >= 80 ? '#4caf50' : item.score >= 50 ? '#ff9800' : '#f44336'"
                dark
                small
                class="font-weight-bold"
              >
                {{ item.score }}%
              </v-chip>
            </template>
            <template v-slot:[`item.Preview`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="ca-action-btn ca-action-view"
                    v-on="on"
                    v-bind="attrs"
                    @click="$router.push({ name: 'AssessmentResults', query: item })"
                  >
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/9912/9912341.png"
                      width="20"
                      height="20"
                      alt="evaluation"
                    />
                  </v-btn>
                </template>
                <span>{{ $t('Results') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- New Assessment Dialog -->
    <v-dialog v-model="showNewAssessmentDialog" max-width="480">
      <v-card class="ca-dialog-card">
        <div class="ca-dialog-header">
          <v-icon color="white" size="28" class="mr-3">mdi-plus-circle</v-icon>
          <span class="ca-dialog-title">{{ $t('conformityAssessment.newAssessment') }}</span>
        </div>
        <v-card-text class="pa-6">
          <v-alert type="info" dense outlined class="mb-5 ca-dialog-alert">
            {{ $t('conformityAssessment.infoMessage') }}
          </v-alert>
          <v-btn
            block
            large
            class="ca-dialog-btn ca-dialog-btn-primary mb-3"
            @click="$router.push({ name: 'SubjectOfCertification' })"
          >
            <v-icon class="mr-3" size="22">mdi-plus-box</v-icon>
            {{ $t('conformityAssessment.buttonsoc') }}
          </v-btn>
          <v-btn
            block
            large
            class="ca-dialog-btn ca-dialog-btn-secondary mb-2"
            @click="$router.push({ name: 'SacBuilder' })"
          >
            <v-icon class="mr-3" size="22">mdi-checkbox-marked-outline</v-icon>
            {{ $t('Select a Subject of Certification') }}
          </v-btn>
        </v-card-text>
        <v-card-actions class="px-6 pb-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn text class="ca-dialog-close" @click="showNewAssessmentDialog = false">
            <v-icon left size="18">mdi-close</v-icon>
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import Loader from '@/components/helpers/Loader.vue';
// import Header from '@/components/helpers/Header';
import Soc from '@/modules/soc/api/soc.js';
import securityAssuranceCaseApi from '@/modules/sac/api/securityAssuranceCaseApi';
import EventBus from '@/helpers/event-bus.js';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'ConformityAssessment',
  components: {
    Loader,
    // Header,
    Breadcrumbs,
  },
  data() {
    return {
      socData: [],
      sacData: [],
      sacScores: null,
      loading: false,
      showNewAssessmentDialog: false,
      assets: [],
      searchQuery: '',
      searchField: 'all',
    };
  },
  computed: {
    getItemsPerPageText() {
      return this.$t('conformityAssessment.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('conformityAssessment.headers.SoC'),
          value: 'soc',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('conformityAssessment.headers.SAC'),
          value: 'sac',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('Score'),
          value: 'score',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('Results'),
          value: 'Preview',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('conformityAssessment.headers.SoC'), value: 'soc' },
        { text: this.$t('conformityAssessment.headers.SAC'), value: 'sac' },
        { text: this.$t('Score'), value: 'score' },
      ];
    },
  },
  async created() {
    await this.loadAllData();
  },
  methods: {
    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([this.fetchSocs(), this.fetchSacs(), this.getSacScores()]);
        this.createAssetsList();
      } catch (error) {
        console.error('Error loading all data:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchSocs() {
      try {
        const response = await Soc.all();
        if (response && response.data) {
          this.socData = response.data;
        } else {
          console.error('Invalid response from Soc.all()');
        }
      } catch (error) {
        console.error('Error fetching socs:', error);
      }
    },
    async fetchSacs() {
      try {
        const { data: rawSecurityAssuranceCases } = await securityAssuranceCaseApi.all();
        this.sacData = rawSecurityAssuranceCases;
      } catch (error) {
        if (error.response?.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      }
    },
    async getSacScores() {
      try {
        const { data: scores } = await securityAssuranceCaseApi.getScores();
        this.sacScores = scores;
      } catch (error) {
        if (error.response?.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      }
    },
    createAssetsList() {
      this.assets = this.socData
        .map(soc => {
          const sac = this.sacData.find(sac => sac.socVersionId === soc.currentVersionId);
          if (!sac) return null;
          const score = this.sacScores?.find(score => score.id === sac.id);
          if (!score) return null;

          return {
            socId: soc.id,
            soc: soc.name,
            sac: sac.name,
            score: score.score,
            standardFamily: soc.standardFamily, // Include standard family info
            sacId: sac.id, // Include SAC ID
          };
        })
        .filter(item => item !== null);
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'Preview' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
  },
};
</script>

<style scoped>
/* ============================================
   CONFORMITY ASSESSMENT - Modern Design System
   ============================================ */

.ca-breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 28px;
}

.ca-breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Hero Card */
.ca-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.ca-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.ca-hero-header-content {
  flex: 1;
}

.ca-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.ca-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.ca-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;
}

.ca-new-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  transition: all 0.3s ease !important;
  backdrop-filter: blur(4px);
}

.ca-new-btn:hover {
  background: rgba(255, 255, 255, 0.35) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

.ca-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.02rem;
}

/* Feature Boxes */
.ca-feature-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 14px;
  padding: 22px;
  height: 100%;
  border: 1px solid #e8eef3;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.ca-feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 64, 94, 0.12);
  border-color: #0097a7;
}

.ca-feature-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.ca-feature-icon-box {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.ca-icon-info {
  background: linear-gradient(135deg, #1976d2, #42a5f6);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.ca-icon-success {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.3);
}

.ca-feature-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a237e;
}

.ca-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ca-feature-list li {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  font-size: 0.92rem;
  color: #455a64;
  line-height: 1.5;
}

.ca-workflow-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ca-workflow-list li {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  font-size: 0.92rem;
  color: #455a64;
  line-height: 1.5;
}

.ca-step-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00405e, #0097a7);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Table Card */
.ca-table-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.ca-table-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.ca-table-header-text {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ca-count-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.ca-search-field {
  min-width: 150px;
}

.ca-data-table {
  border-radius: 0 0 16px 16px;
}

.ca-data-table ::v-deep thead th {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%) !important;
  color: #1a237e !important;
  font-weight: 700 !important;
  font-size: 0.82rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e0e0e0 !important;
}

.ca-data-table ::v-deep tbody tr {
  transition: all 0.2s ease;
}

.ca-data-table ::v-deep tbody tr:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%) !important;
}

.ca-data-table ::v-deep tbody td {
  font-size: 0.9rem;
  color: #37474f;
  padding: 12px 16px;
}

/* Action Buttons */
.ca-action-btn {
  transition: all 0.3s ease !important;
}

.ca-action-view {
  color: #1976d2 !important;
}

.ca-action-view:hover {
  background: rgba(25, 118, 210, 0.1) !important;
  transform: scale(1.15);
}

/* Dialog */
.ca-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.ca-dialog-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.ca-dialog-title {
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
}

.ca-dialog-alert {
  border-radius: 10px !important;
}

.ca-dialog-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  font-size: 0.95rem !important;
  transition: all 0.3s ease !important;
}

.ca-dialog-btn-primary {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%) !important;
  color: white !important;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.35) !important;
}

.ca-dialog-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(25, 118, 210, 0.5) !important;
}

.ca-dialog-btn-secondary {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%) !important;
  color: white !important;
  box-shadow: 0 4px 16px rgba(123, 31, 162, 0.35) !important;
}

.ca-dialog-btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(123, 31, 162, 0.5) !important;
}

.ca-dialog-close {
  color: #78909c !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.ca-dialog-close:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

/* Responsive */
@media (max-width: 960px) {
  .ca-hero-header {
    padding: 20px;
  }
  .ca-hero-title {
    font-size: 1.4rem;
  }
}
</style>
